import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Table } from "reactstrap";
import { findFirstAndLastOpenDates, getCheckboxElement, getDateElement } from "../helpers";
import { getCoreRowModel, useReactTable, flexRender } from "@tanstack/react-table";

import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/nl";

import AvailabilityOption from "./AvailabilityOption";
import { generateControlTableColumns, availabilityValues } from "../helpers";
import { useInitialDates } from "../hooks/useInitialDates";
import { useControlshermContext } from "../context/ControlshermContext";

dayjs.locale("nl");
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

export default function ControlTable({
	hideRequested,
	setHideRequested,
	hideUnselected,
	setHideUnselected,
	availabilityFilter,
	setAvailabilityFilter,
}) {
	const { dates, setDates, hasUpcomingDays } = useControlshermContext();
	const initialData = useInitialDates(dates);
	const [data, setData] = useState([]);

	const columns = useMemo(() => {
		return generateControlTableColumns(initialData);
	}, [initialData]);
const handleDatesSelection = (shift, weekday, value, applyToAll = false) => {
  const today = dayjs();
  const currentWeek = today.isoWeek();
  const currentYear = today.year();

  
  const WEEKDAY_MAP = {
    mo: "ma",
    mm: "ma",
    dm: "di",
    tm: "di",
    wo: "wo",
    wm: "wo",
    do: "do",
    dm2: "do",
    fr: "vr",
    vrm: "vr",
  };

  
  const normalizedColumnWeekday = WEEKDAY_MAP[weekday.toLowerCase()] || weekday.toLowerCase();

  const updatedDates = dates.map((d) => {
    const parsedDate = dayjs(d.day, "DD-MM-YYYY");
    const isCurrentOrFuture =
      parsedDate.year() > currentYear ||
      (parsedDate.year() === currentYear && parsedDate.isoWeek() >= currentWeek);

    return {
      ...d,
      data: Object.fromEntries(
        Object.entries(d.data).map(([shiftKey, shiftObj]) => {
          if (!shiftObj) return [shiftKey, shiftObj];

         
          const normalizedShiftObjWeekday = WEEKDAY_MAP[shiftObj.weekday] || shiftObj.weekday;

        
          const matchesColumn =
            applyToAll ||
            (normalizedShiftObjWeekday === normalizedColumnWeekday && shiftObj.shift === shift);

          if (matchesColumn) {
            return [
              shiftKey,
              {
                ...shiftObj,
                isSelected:
                  shiftObj.status === "Openstaand" &&
                  (hasUpcomingDays ? isCurrentOrFuture : true)
                    ? value
                    : false,
              },
            ];
          }
          return [shiftKey, shiftObj];
        })
      ),
    };
  });
  setDates(updatedDates);
};


useEffect(() => {
	const usedIcons = new Set();

	dates.forEach((day) => {
		["Ochtend", "Middag"].forEach((shift) => {
			const obj = day.data?.[shift];
			if (!obj) return;
			if (obj.availability && obj.isSelected) {
				usedIcons.add(obj.availability);
			}
		});
	});

	setAvailabilityFilter([...usedIcons]);
}, [dates]);



	const table = useReactTable({
		data: data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		meta: {
			dates,
			handleDatesSelection,
		},
	});

	useEffect(() => {
		setData([initialData]);
	}, [initialData]);

	const firstAndLastDates = useMemo(() => {
		return findFirstAndLastOpenDates(dates);
	}, [dates]);

	return (
		<Card className="controlsherm__table">
			<CardBody>
				<div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
					{Array.isArray(dates) &&
						dates.length > 0 &&
						getDateElement("Startdatum", firstAndLastDates[0]?.day ?? "--")}
					{Array.isArray(dates) &&
						dates.length > 0 &&
						getDateElement("Einddatum", firstAndLastDates[1]?.day ?? "--")}
					{getCheckboxElement(
						hideRequested,
						setHideRequested,
						"Verberg ingeplande / aangevraagde dagen",
						"hide-requested"
					)}
					{getCheckboxElement(
						hideUnselected,
						setHideUnselected,
						"Verberg alle niet geselecteerde dagen",
						"hide-selected"
					)}
				</div>

				<Table className="controlsherm__table controlsherm__table--settings table-hover">
					<thead>
						{table.getHeaderGroups().map((headerGroup) => {
							return (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										const groupsArray = header?.column?.parent?.columns;
										const headerItem = groupsArray?.find(
											(i) => i?.id === header?.id
										);
										const index = groupsArray?.indexOf(headerItem);
										const isLastInGroup =
											index + 1 === groupsArray?.length ||
											groupsArray === undefined;

										return (
											<th
												key={header?.id}
												colSpan={header.colSpan}
												className={isLastInGroup ? "border-end" : ""}
											>
												{flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
											</th>
										);
									})}
								</tr>
							);
						})}
					</thead>
					<tbody>
						{table.getRowModel().rows.map((row) => {
							return (
								<tr key={row?.id}>
									{row.getVisibleCells().map((cell) => {
										const groupColumns = cell.column.parent?.columns ?? [];
										const isLastInGroup =
											groupColumns[groupColumns.length - 1] === cell.column;
										return (
											<td
												key={cell?.id}
												className={isLastInGroup ? "border-end" : ""}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</Table>

				<div className="d-flex gap-5 align-items-center">
					<span className="fs-6 fw-semibold mt-4">Beschikbaarheid</span>

					{availabilityValues.map((val) => (
						<AvailabilityOption
							key={val}
							value={val}
							availabilityFilter={availabilityFilter}
							setAvailabilityFilter={setAvailabilityFilter}
						/>
					))}
				</div>
			</CardBody>
		</Card>
	);
}
